import { Attachment } from "../contexts/SettingsContext";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

/**
 * Convert a zero-based column index to its Excel column letters (e.g. 6 -> "G", 27 -> "AB").
 */
export const columnLetter = (index: number): string => {
  let n = index + 1;
  let result = "";
  while (n > 0) {
    const remainder = (n - 1) % 26;
    result = LETTERS.charAt(remainder) + result;
    n = Math.floor((n - 1) / 26);
  }
  return result;
};

/**
 * Return column letters from a zero-based start index to a zero-based end index (inclusive).
 */
export const columnLettersFromTo = (startIndex: number, endIndex: number): string[] => {
  const letters: string[] = [];
  for (let i = startIndex; i <= endIndex; i++) {
    letters.push(columnLetter(i));
  }
  return letters;
};

const isNumeric = (value: unknown): boolean => {
  if (typeof value === "number") return true;
  if (typeof value === "string" && value.trim() !== "") {
    return !Number.isNaN(Number(value));
  }
  return false;
};

/**
 * Heuristic: the first row is treated as a header row when every cell is a
 * non-empty text value (not numeric) and at least one of the remaining rows
 * contains a numeric value.
 */
export const looksLikeHeaderRow = (firstRow: unknown[], restRows: unknown[][]): boolean => {
  if (!firstRow || firstRow.length === 0) return false;

  const firstRowIsText =
    firstRow.every((cell) => typeof cell === "string" && cell.trim() !== "") &&
    !firstRow.some((cell) => isNumeric(cell));

  if (!firstRowIsText) return false;

  return restRows.some((row) => row.some((cell) => isNumeric(cell)));
};

const escapeCsvCell = (value: any): string => {
  if (value === null || value === undefined) return "";
  const str = String(value);

  const needsQuotes =
    str.includes(",") ||
    str.includes('"') ||
    str.includes("\n") ||
    str.includes("\r") ||
    str.trim().startsWith(" ") ||
    str.trim().endsWith(" ");

  const escapedStr = str.replace(/"/g, '""');

  return needsQuotes ? `"${escapedStr}"` : escapedStr;
};

export const rowsToCsv = (rows: unknown[][]): string => {
  return rows.map((row) => row.map((cell) => escapeCsvCell(cell)).join(",")).join("\n");
};

/**
 * Parse a CSV string into a 2D array of strings.
 */
export const csvToRows = (content: string): string[][] => {
  if (!content) return [];

  const lines = content.split("\n");
  const result: string[][] = [];

  for (const line of lines) {
    if (line.trim() === "") continue;

    const row: string[] = [];
    let currentCell = "";
    let insideQuotes = false;
    let escapeNext = false;

    for (const char of line) {
      if (escapeNext) {
        currentCell += char;
        escapeNext = false;
      } else if (char === "\\") {
        escapeNext = true;
      } else if (char === '"') {
        insideQuotes = !insideQuotes;
      } else if (char === "," && !insideQuotes) {
        row.push(currentCell);
        currentCell = "";
      } else {
        currentCell += char;
      }
    }

    row.push(currentCell);
    result.push(row);
  }

  return result;
};

/**
 * Build a structured Attachment from an Excel selected range.
 *
 * @param values      Loaded range values (2D array).
 * @param address     Loaded range address, e.g. "G6:I8" or "Sheet1!G6:I8".
 * @param rowIndex    Zero-based top row index of the range.
 * @param columnIndex Zero-based left column index of the range.
 * @param columnCount Number of columns in the range.
 */
export const buildAttachment = (
  values: unknown[][],
  address: string,
  rowIndex: number,
  columnIndex: number,
  columnCount: number
): Attachment => {
  const dataRows = values.length ? values : [];
  const hasHeaderRow = looksLikeHeaderRow(dataRows[0] || [], dataRows.slice(1));
  const columnLetters = columnLettersFromTo(columnIndex, columnIndex + columnCount - 1);

  let headers: string[];
  let bodyRows: unknown[][];

  if (hasHeaderRow) {
    headers = dataRows[0].map((cell) => (cell === null || cell === undefined ? "" : String(cell)));
    bodyRows = dataRows.slice(1);
  } else {
    headers = columnLetters;
    bodyRows = dataRows;
  }

  const firstDataRow = rowIndex + (hasHeaderRow ? 2 : 1);

  return {
    range: address,
    headers,
    columnLetters,
    hasHeaderRow,
    firstDataRow,
    dataCsv: rowsToCsv(bodyRows),
  };
};
